import type { RealtimeChannel } from '@supabase/supabase-js'
import { channelInstanceTopic, getSupabaseClient, unsubscribeChannel } from '../Supabase/supabaseClient'

export const MESSAGES_CHANNEL_PREFIX = 'support-messages'

export interface MessagesFilter {
  event: '*'
  schema: 'public'
  table: 'support_message_changes'
  filter: string
}

/**
 * Subscribe to Plan 1's body-free signal table (public.support_message_changes),
 * filtered by conversation_id. The payload carries no message content — on any
 * event the caller refetches the thread through the org-scoped backend API.
 */
export const buildMessagesFilter = (conversationId: string): MessagesFilter => ({
  event: '*',
  schema: 'public',
  table: 'support_message_changes',
  filter: `conversation_id=eq.${conversationId}`,
})

/**
 * `onChange` should trigger an API refetch — do NOT use the raw payload.
 * Returns an unsubscribe function (no-op if Supabase is unconfigured).
 * `channelKey` namespaces the channel topic so independent subscribers (the open
 * panel vs the app-level unread watcher) don't collide on the same Supabase topic.
 */
export const subscribeToConversationMessages = (
  conversationId: string,
  onChange: () => void,
  channelKey = 'main'
): (() => void) => {
  const client = getSupabaseClient()
  if (!client) return () => {}

  const channel: RealtimeChannel = client
    // A fresh instance per subscribe (ATL-1405): two live subscribers on one key would otherwise
    // share one channel object, and the next reconnect rejects it permanently.
    .channel(channelInstanceTopic(`${MESSAGES_CHANNEL_PREFIX}-${channelKey}-${conversationId}`))
    .on('postgres_changes', buildMessagesFilter(conversationId), () => {
      onChange()
    })
    .subscribe()

  return () => unsubscribeChannel(channel)
}
